<template>
  <div
    class="upload-zone"
    :class="{
      'upload-zone--dragover': isDragOver,
      'upload-zone--has-preview': previewUrl,
    }"
    @drop="handleDrop"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @click="triggerFileInput"
  >
    <!-- 文件输入 -->
    <input
      ref="fileInputRef"
      type="file"
      :accept="accept"
      class="upload-zone__input"
      @change="handleFileChange"
    />

    <!-- 上传区域内容 -->
    <slot name="default" :previewUrl="previewUrl"> </slot>
  </div>
</template>

<script lang="ts" setup>
import { message } from '@rendora/common/composables/message';
import { ref } from 'vue';

// Props
interface Props {
  currentPreview?: string;
  accept?: string;
  maxSize?: number;
  msg?: {
    type: string;
    size: string;
  };
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  currentPreview: '',
  maxSize: 100 * 1024 * 1024,
  simpleUpload: () => {},
  accept: 'image/jpeg,image/png,image/jpg',
  disabled: false,
});

// Emits
const emit = defineEmits<{
  fileSelect: [file: File];
  beforeUpload: [];
}>();

// Refs
const fileInputRef = ref<HTMLInputElement>();
const isDragOver = ref(false);
const previewUrl = ref<string>(props.currentPreview);

// Methods
const triggerFileInput = () => {
  emit('beforeUpload');
  if (props.disabled) return;

  fileInputRef.value?.click();
};

// 图片大小不能超过3MB，当前XXMB
const isSizeValid = (file: File) => {
  const sizeMessage = props.msg?.size || '';
  if (file.size > props.maxSize) {
    const currentSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    message.error(`${sizeMessage}，当前${currentSizeMB}MB`);
    return false;
  }
  return true;
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  // 检查文件是否存在
  if (!file) {
    message.error('请选择文件');
    return;
  }

  // 检查文件大小是否有效
  if (!isSizeValid(file)) {
    return;
  }

  if (file) {
    handleFileSelect(file);
  }

  // 清空 input 值，允许重复选择同一文件
  target.value = '';
};

const handleFileSelect = (file: File) => {
  try {
    // 创建预览
    const reader = new FileReader();
    reader.onload = e => {
      previewUrl.value = e.target?.result as string;
      emit('fileSelect', file);
    };
    reader.readAsDataURL(file);
  } catch (error) {
    console.error('文件选择失败:', error);
    previewUrl.value = '';
  }
};

const handleDrop = (event: DragEvent) => {
  emit('beforeUpload');
  if (props.disabled) return;
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handleFileSelect(files[0]);
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;
};

// 暴露方法给父组件
defineExpose({
  triggerFileInput,
  previewUrl,
});
</script>

<style lang="scss" scoped>
.upload-zone {
  position: relative;
  border: 1px dashed var(--x-back-4);
  border-radius: var(--x-radius-medium);
  background: var(--x-back-2);
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--x-purple-6);
    background: var(--x-purple-1);
    color: var(--x-purple-6);
  }

  &--dragover {
    border-color: var(--x-purple-6);
    background: var(--x-purple-1);
    transform: scale(1.02);
  }

  &--uploading {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &--has-preview {
    min-height: 300px;
    color: #666;
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
}

.upload-zone-content {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-zone-default {
  text-align: center;
}

.upload-zone-content-text {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: var(--x-weight-medium);
}

.upload-zone-content-hint {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.upload-zone-preview {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--x-radius-medium);
  overflow: hidden;

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    visibility: hidden;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover .upload-zone-preview__overlay {
    opacity: 1;
  }

  &__icon {
    margin-bottom: 8px;
    width: 32px;
    height: 32px;
    color: white;
  }

  &__text {
    color: white;
    font-size: 14px;
    margin: 0;
    font-weight: var(--x-weight-medium);
  }
}

.upload-zone-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  padding: 12px;

  &__bar {
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }

  &__fill {
    height: 100%;
    background: #1890ff;
    border-radius: 2px;
    animation: progress-animation 2s ease-in-out infinite;
  }

  &__text {
    color: white;
    font-size: 12px;
    margin: 0;
    text-align: center;
  }
}

@keyframes progress-animation {
  0% {
    width: 0%;
  }
  50% {
    width: 70%;
  }
  100% {
    width: 100%;
  }
}
</style>
